import SecureLS from 'secure-ls';

const ACCESS_TOKEN_KEY = 'safe-minds.access-token';
const REFRESH_TOKEN_KEY = 'safe-minds.refresh-token';
const THEME_KEY = 'safe-minds.theme';
const CHILD_ACCESS_KEY = 'safe-minds.child-access';

type StorageLike = {
  get: (key: string) => unknown;
  set: (key: string, value: unknown) => void;
  remove: (key: string) => void;
};

let lsInstance: StorageLike | null = null;

function createMemoryStorage(): StorageLike {
  const store = new Map<string, unknown>();
  return {
    get: (key) => store.get(key),
    set: (key, value) => {
      store.set(key, value);
    },
    remove: (key) => {
      store.delete(key);
    },
  };
}

function getStorage(): StorageLike {
  if (lsInstance) return lsInstance;

  const browserGlobals = globalThis as typeof globalThis & {
    localStorage?: unknown;
  };
  const hasBrowserStorage =
    typeof globalThis === 'object' && 'localStorage' in globalThis && Boolean(browserGlobals.localStorage);

  if (hasBrowserStorage) {
    lsInstance = new SecureLS({
      encodingType: 'aes',
      isCompression: false,
    }) as StorageLike;
    return lsInstance;
  }

  lsInstance = createMemoryStorage();
  return lsInstance;
}

export function getAccessToken(): string | null {
  const storage = getStorage();
  try {
    return (storage.get(ACCESS_TOKEN_KEY) as string) ?? null;
  } catch (error) {
    storage.remove(ACCESS_TOKEN_KEY);
    return null;
  }
}

export function setAccessToken(token: string): void {
  getStorage().set(ACCESS_TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  getStorage().remove(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  const storage = getStorage();
  try {
    return (storage.get(REFRESH_TOKEN_KEY) as string) ?? null;
  } catch (error) {
    storage.remove(REFRESH_TOKEN_KEY);
    return null;
  }
}

export function setRefreshToken(token: string): void {
  getStorage().set(REFRESH_TOKEN_KEY, token);
}

export function clearRefreshToken(): void {
  getStorage().remove(REFRESH_TOKEN_KEY);
}

export type ThemePreference = 'light' | 'dark' | 'system';

export function getThemePreference(): ThemePreference {
  const raw = getStorage().get(THEME_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') {
    return raw;
  }
  return 'system';
}

export function setThemePreference(theme: ThemePreference): void {
  getStorage().set(THEME_KEY, theme);
}

export function clearThemePreference(): void {
  getStorage().remove(THEME_KEY);
}

export interface ChildAccessPersistedState {
  granted: boolean;
  code?: string;
  sessionId?: string;
  childId?: string;
  childName?: string;
  grantedAt: number;
  expiresAt?: string;
}

export function getChildAccessState(): ChildAccessPersistedState | null {
  const storage = getStorage();
  try {
    return (storage.get(CHILD_ACCESS_KEY) as ChildAccessPersistedState) ?? null;
  } catch {
    storage.remove(CHILD_ACCESS_KEY);
    return null;
  }
}

export function setChildAccessState(state: ChildAccessPersistedState | null): void {
  const storage = getStorage();
  if (!state) {
    storage.remove(CHILD_ACCESS_KEY);
    return;
  }
  storage.set(CHILD_ACCESS_KEY, state);
}
